import { useState, useEffect, useRef } from "react";
import { api } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Volume2, VolumeX, MessageCircle, Twitter, Send, Copy, Music } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function PromoVideoDialog({ open, onOpenChange, withSound = false }) {
  const videoRef = useRef(null);
  const { t } = useLocale();
  const [muted, setMuted] = useState(true);
  const [refCode, setRefCode] = useState(null);

  useEffect(() => {
    if (open) {
      const m = !withSound;
      setMuted(m);
      const timer = setTimeout(() => {
        if (videoRef.current) videoRef.current.muted = m;
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open, withSound]);

  useEffect(() => {
    if (open && localStorage.getItem("token") && !refCode) {
      api.get("/referrals").then((r) => setRefCode(r.data.referral_code)).catch(() => {});
    }
  }, [open, refCode]);

  const shareUrl = `${window.location.origin}/?film=1${refCode ? `&ref=${refCode}` : ""}`;
  const shareText = `${t("promo.shareMsg")}${refCode ? ` ${t("promo.joinInvite")}` : ""} ${shareUrl}`;
  const enc = encodeURIComponent(shareText);

  const toggleMute = () => {
    const next = !muted;
    if (videoRef.current) videoRef.current.muted = next;
    setMuted(next);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
    } catch {
      /* noop */
    }
    toast.success(refCode ? t("promo.copiedInvite") : t("promo.copied"));
  };

  const tileCls =
    "w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-card border-white/10" data-testid="promo-video-dialog">
        <DialogHeader>
          <img src="/logo-neon.png" alt="luxe-meet.com" className="h-16 w-auto mx-auto object-contain" data-testid="promo-neon-logo" />
          <DialogTitle className="font-['Outfit'] text-center">{t("promo.title")}</DialogTitle>
          <DialogDescription className="text-center">{t("promo.desc")}</DialogDescription>
        </DialogHeader>
        {open && (
          <div className="relative">
            <video
              ref={videoRef}
              src={`${process.env.REACT_APP_BACKEND_URL}/api/media/promo.mp4`}
              poster="/promo-poster.jpg"
              controls
              autoPlay
              muted
              playsInline
              className="w-full rounded-xl border border-white/10 bg-black"
              data-testid="promo-video"
            />
            {muted && (
              <button
                type="button"
                onClick={toggleMute}
                className="absolute top-3 right-14 h-10 px-3.5 rounded-full bg-black/60 backdrop-blur-sm border border-amber-400/40 flex items-center gap-1.5 hover:bg-black/80 transition-colors animate-pulse"
                data-testid="promo-sound-hint"
              >
                <Music className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-medium text-amber-300 whitespace-nowrap">{t("promo.tapSound")}</span>
              </button>
            )}
            <button
              type="button"
              onClick={toggleMute}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center hover:bg-black/80 transition-colors"
              title={muted ? t("promo.unmute") : t("promo.mute")}
              data-testid="promo-mute-toggle"
            >
              {muted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-amber-400" />}
            </button>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="promo-share-row">
          <p className="text-xs text-muted-foreground">
            {refCode ? t("promo.shareInvite") : t("promo.shareFriends")}
          </p>
          <div className="flex gap-2">
            <a href={`https://wa.me/?text=${enc}`} target="_blank" rel="noreferrer" className={tileCls} title="WhatsApp" data-testid="promo-share-whatsapp">
              <MessageCircle className="w-4 h-4 text-green-400" />
            </a>
            <a href={`https://twitter.com/intent/tweet?text=${enc}`} target="_blank" rel="noreferrer" className={tileCls} title="X" data-testid="promo-share-x">
              <Twitter className="w-4 h-4 text-sky-400" />
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(t("promo.shareTg"))}`}
              target="_blank"
              rel="noreferrer"
              className={tileCls}
              title="Telegram"
              data-testid="promo-share-telegram"
            >
              <Send className="w-4 h-4 text-cyan-400" />
            </a>
            <button type="button" onClick={copyLink} className={tileCls} title="Copy link" data-testid="promo-share-copy">
              <Copy className="w-4 h-4 text-amber-400" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
